import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { receiveAsset } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Package, Loader2 } from 'lucide-react';
import { getStatusLabel, getStatusColor } from '../lib/utils';
import PhotoUpload from './PhotoUpload';

export default function ReceiveWorkflow({ open, onClose, asset }) {
  const { selectedLocation, getCategoryName, getLocationName } = useApp();
  const [loading, setLoading] = useState(false);
  const [condition, setCondition] = useState('WORKING');
  const [notes, setNotes] = useState('');
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [photos, setPhotos] = useState([]);

  const resetForm = () => {
    setCondition('WORKING');
    setNotes('');
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setPhotos([]);
  };

  const handleReceive = async () => {
    if (photos.length === 0) {
      toast.error('Please add at least one photo documenting the asset condition upon arrival');
      return;
    }
    
    setLoading(true);
    try {
      await receiveAsset(asset.id, selectedLocation, {
        condition,
        notes: notes || undefined,
        event_date: new Date(receiveDate).toISOString(),
        photos,
      });
      toast.success('Asset received successfully!');
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive asset');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Receive Asset</DialogTitle>
          <DialogDescription>
            <span className="font-mono font-bold">{asset.barcode}</span> - {getCategoryName(asset.category_id)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Asset Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Current Status</span>
              <Badge className={getStatusColor(asset.asset_status)}>
                {getStatusLabel(asset.asset_status)}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">From Location</span>
              <span className="font-medium">{getLocationName(asset.current_location_id)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Receiving At</span>
              <span className="font-medium text-blue-600">{getLocationName(selectedLocation)}</span>
            </div>
          </div>

          {/* Condition Selection */}
          <div>
            <Label className="mb-2 block">Condition After Receiving *</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={condition === 'WORKING' ? 'default' : 'outline'}
                className={`flex-1 h-14 ${condition === 'WORKING' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => setCondition('WORKING')}
                data-testid="receive-condition-working"
              >
                WORKING
              </Button>
              <Button
                type="button"
                variant={condition === 'BROKEN' ? 'default' : 'outline'}
                className={`flex-1 h-14 ${condition === 'BROKEN' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                onClick={() => setCondition('BROKEN')}
                data-testid="receive-condition-broken"
              >
                BROKEN
              </Button>
            </div>
          </div>

          {/* Date */}
          <div>
            <Label>Date Received</Label>
            <Input
              type="date"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              data-testid="receive-date-input"
            />
          </div>

          {/* Notes */}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about the received asset..."
              data-testid="receive-notes-input"
            />
          </div>

          {/* Photo Upload */}
          <PhotoUpload
            photos={photos}
            onPhotosChange={setPhotos}
            required={true}
            entityType="receive"
            entityId={asset?.id || 'temp'}
            label="Receiving Photos"
            helpText="Document the asset condition upon arrival"
          />

          <Button 
            onClick={handleReceive} 
            disabled={loading || photos.length === 0}
            className={`w-full ${photos.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
            data-testid="receive-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
            Receive Asset
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
